import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { NIKO_API_URL } from '../tokens/api.tokens';
import {
  CreateMcpAccessGrantPayload,
  McpAccessGrant,
  McpAccessInvitationOutcome,
  McpAccessListQuery,
  McpAccessListResult,
  McpAccessMutationResult,
  RevokeMcpAccessGrantPayload,
  UpdateMcpAccessTenantsPayload,
} from '../models/mcp-access-grant.model';

/** HTTP surface of "Acceso MCP" (BR-MCP-01..09) - every call is SuperAdmin-only, enforced server-side. */
@Injectable({ providedIn: 'root' })
export class McpAccessService {
  private readonly http = inject(HttpClient);
  private readonly base = inject(NIKO_API_URL);

  list(query: McpAccessListQuery): Observable<McpAccessListResult> {
    let params = new HttpParams().set('page', query.page).set('limit', query.limit);
    if (query.search) params = params.set('search', query.search);
    if (query.status) params = params.set('status', query.status);
    return this.http.get<McpAccessListResult>(`${this.base}/mcp-access`, { params });
  }

  create(payload: CreateMcpAccessGrantPayload): Observable<McpAccessMutationResult> {
    return this.http.post<McpAccessMutationResult>(`${this.base}/mcp-access`, payload);
  }

  updateTenants(id: string, payload: UpdateMcpAccessTenantsPayload): Observable<McpAccessGrant> {
    return this.http.patch<McpAccessGrant>(`${this.base}/mcp-access/${id}/tenants`, payload);
  }

  revoke(id: string, payload: RevokeMcpAccessGrantPayload): Observable<McpAccessGrant> {
    return this.http.patch<McpAccessGrant>(`${this.base}/mcp-access/${id}/revoke`, payload);
  }

  reactivate(id: string): Observable<McpAccessGrant> {
    return this.http.patch<McpAccessGrant>(`${this.base}/mcp-access/${id}/reactivate`, {});
  }

  resendInvitation(id: string): Observable<McpAccessMutationResult> {
    return this.http.post<McpAccessMutationResult>(
      `${this.base}/mcp-access/${id}/resend-invitation`,
      {},
    );
  }

  getInvitationLink(id: string): Observable<McpAccessInvitationOutcome> {
    return this.http.post<McpAccessInvitationOutcome>(
      `${this.base}/mcp-access/${id}/invitation-link`,
      {},
    );
  }

  /** `sendInvitation` may re-mail per the design; the caller decides when to invoke it (resync repairs are separate). */
  resync(id: string): Observable<McpAccessMutationResult> {
    return this.http.post<McpAccessMutationResult>(`${this.base}/mcp-access/${id}/resync`, {});
  }
}
